import React, { useRef, useState, useCallback } from "react";

/**
 * Small "ⓘ" icon that reveals an explanatory popover on hover/focus.
 * Used to explain how a computed section (e.g. a discovery category) works.
 *
 * The popover is viewport-aware: if a centered popover would run off the
 * left/right edge of the screen (edge tiles, mobile), it shifts back into view
 * so the text is always fully visible.
 */
export default function InfoTooltip({ text, className = "" }) {
  const tipRef = useRef(null);
  const [shift, setShift] = useState(0);

  const reposition = useCallback(() => {
    const el = tipRef.current;
    if (!el) return;
    el.style.transform = "translateX(-50%)";
    const rect = el.getBoundingClientRect();
    const margin = 8;
    let s = 0;
    if (rect.left < margin) s = margin - rect.left;
    else if (rect.right > window.innerWidth - margin) {
      s = window.innerWidth - margin - rect.right;
    }
    setShift(s);
  }, []);

  return (
    <span
      className={`relative inline-flex group ${className}`}
      onMouseEnter={reposition}
      onFocus={reposition}
    >
      <button
        type="button"
        aria-label="More info"
        className="h-4 w-4 rounded-full border border-current text-[10px] leading-none flex items-center justify-center opacity-70 hover:opacity-100 transition-opacity"
      >
        i
      </button>
      <span
        ref={tipRef}
        role="tooltip"
        style={{ left: "50%", transform: `translateX(calc(-50% + ${shift}px))` }}
        className="pointer-events-none absolute bottom-full mb-2 w-60 max-w-[calc(100vw-16px)] rounded-xl border border-brand-gold/50 bg-surface-light/98 backdrop-blur-md p-3 text-xs leading-relaxed text-white/90 font-normal opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity duration-150 z-[100] shadow-card"
      >
        {text}
      </span>
    </span>
  );
}
